// freeCredits.js
// Anonymous usage limit. Signed-in users bypass entirely (unlimited locally;
// server-side billing happens elsewhere). Anonymous users get FREE_CREDITS_INITIAL
// total message sends before the out-of-credits modal blocks further sends.

const TOKEN_KEY = 'aria_session_token';
const CREDITS_KEY = 'aria_free_credits_remaining';

export const FREE_CREDITS_INITIAL = 12;

export function isLoggedIn() {
  try { return !!localStorage.getItem(TOKEN_KEY); } catch (_) { return false; }
}

export function getCreditsRemaining() {
  try {
    const raw = localStorage.getItem(CREDITS_KEY);
    if (raw === null) return FREE_CREDITS_INITIAL;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? Math.max(0, n) : FREE_CREDITS_INITIAL;
  } catch (_) {
    return FREE_CREDITS_INITIAL;
  }
}

export function decrementCredits() {
  // The preview sandbox is not a visitor — nothing to spend. Returning the
  // current figure without writing keeps the counter parked where it is, so
  // the meter never counts down and never reaches the modal.
  if (isFirstRunPreview()) return getCreditsRemaining();
  const next = Math.max(0, getCreditsRemaining() - 1);
  try { localStorage.setItem(CREDITS_KEY, String(next)); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent('aria-credits-changed', { detail: { remaining: next } })); } catch (_) {}
  return next;
}

export function resetCredits() {
  try { localStorage.setItem(CREDITS_KEY, String(FREE_CREDITS_INITIAL)); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent('aria-credits-changed', { detail: { remaining: FREE_CREDITS_INITIAL } })); } catch (_) {}
}

// ── THE PREVIEW SANDBOX IS UNLIMITED (Braeden 9/6) ────────────────────────
//
//   "i need unlimited usage on the new tab, so i can use the onboarding as
//    much as i want THATS WHAT ITS FOR ... its like me retrying the onboarding
//    so i can make it perfect"
//
// ?firstrun=new (firstRunPreview.jsx) already mints a throwaway stranger so
// the replay is honest. But it mints one with a REAL visitor's allowance: ten
// sends, then the out-of-credits modal. Onboarding is a conversation — the
// hello, the problem, the questions, the solve — so ten sends is roughly one
// pass, and tuning it meant hitting a paywall in the middle of the thing being
// tuned and restarting to clear it.
//
// So while the sandbox flag is up, the local counter stops applying. This is
// deliberately the ONLY thing lifted:
//
//   • It is client-side, so it grants nothing a person could not already do by
//     clearing their own localStorage. No public bypass is opened.
//   • The SERVER still meters the throwaway id normally (planPolicy's free
//     grant, and middleware/ariaTurnCharge above the lanes). The engines stay
//     protected; at the free plan's grant that is thousands of turns, so it
//     never lands mid-replay the way ten sends did.
//   • It is scoped to `aria_firstrun_preview`, which only exists inside the
//     sandbox and is cleared by Exit — a real visitor can never be in it.
//
// Read straight from storage rather than importing firstRunPreview.jsx: this
// module is imported by App.jsx's own state initializers, and pulling a React
// module in from here just to read one string is a cycle waiting to happen.
const PREVIEW_KEY = 'aria_firstrun_preview';

export function isFirstRunPreview() {
  try { return localStorage.getItem(PREVIEW_KEY) === '1'; } catch (_) { return false; }
}

export function canSendMessage() {
  if (isFirstRunPreview()) return true;
  if (isLoggedIn()) return true;
  return getCreditsRemaining() > 0;
}

// ── WHO GETS THE FIRST-TIME DOOR (Braeden 8/31) ───────────────────────────
//
// "somebodies first experiance, on the website, that is the only spot someone
//  sees the first time button, should never be anywhere else. and its only for
//  people who arnt logged in, and its probably there first time on the site."
//
// Two gates live here because both are storage/environment questions, and the
// render site should read like the sentence he said.

const SEEN_KEY = 'aria_visited_before';

/** The desktop app, not the website. The button is never shown here. */
export function isDesktopApp() {
  try { return typeof window !== 'undefined' && !!window.electronAPI; } catch (_) { return false; }
}

/**
 * First visit to the site, as far as we can tell.
 *
 * Marks on the first call, so the current page load still counts as first and
 * the NEXT one does not. Deliberately errs toward SHOWING it when storage is
 * unreadable (private windows, blocked cookies): a new person who misses the
 * door is a worse outcome than a returning one seeing it twice.
 */
export function isFirstVisit() {
  try {
    if (localStorage.getItem(SEEN_KEY)) return false;
    localStorage.setItem(SEEN_KEY, String(Date.now()));
    return true;
  } catch (_) { return true; }
}
